import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check, Loader2, Sparkles, Zap, FileText, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const GenerationModal = ({ 
  isOpen, 
  onClose, 
  topic, 
  generatedContent, 
  isGenerating, 
  onGenerate 
}) => {
  const [copied, setCopied] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Typewriter effect for generated content
  useEffect(() => {
    if (generatedContent?.content) {
      setIsTyping(true);
      setDisplayedContent('');
      
      const content = generatedContent.content;
      let index = 0;
      
      const typeInterval = setInterval(() => {
        if (index < content.length) {
          setDisplayedContent(prev => prev + content[index]);
          index++;
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
        }
      }, 8); // Fast typing speed

      return () => clearInterval(typeInterval);
    }
  }, [generatedContent]);

  const handleCopy = async () => {
    if (generatedContent) {
      const fullContent = `${generatedContent.title}\n\n${generatedContent.content}\n\nViral Tip: ${generatedContent.viral_tip}`;
      await navigator.clipboard.writeText(fullContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen || !topic) return null;

  const isScript = topic.output === 'Script';

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="generation-modal"
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Modal */}
      <motion.div
        className="generation-modal relative w-full max-w-3xl"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        {/* Header */}
        <div className="generation-modal-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isScript ? (
                <Mic className="w-6 h-6 text-[#CCFF00]" />
              ) : (
                <FileText className="w-6 h-6 text-[#CCFF00]" />
              )}
              <div>
                <h2 className="font-heading text-xl font-bold tracking-tight">
                  Generate {topic.output}
                </h2>
                <p className="text-sm text-white/70 font-body mt-1">
                  {topic.contentType.replace('_', ' ').toUpperCase()} • {topic.audience.toUpperCase()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="generation-modal-content">
          {/* Topic Preview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <p className="font-body font-medium text-gray-900">{topic.topic}</p>
            {topic.viralHook && (
              <p className="mt-2 text-sm text-gray-600 italic">
                Hook: "{topic.viralHook}"
              </p>
            )}
          </div>

          {/* Generate Button or Content */}
          {!generatedContent && !isGenerating && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#CCFF00] to-[#7000FF] flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-600 mb-6 font-body">
                Ready to create your viral {topic.output.toLowerCase()}?
              </p>
              <Button
                onClick={onGenerate}
                className="btn-primary text-base px-8 py-4"
                data-testid="generate-content-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Generate {topic.output}
              </Button>
            </div>
          )}

          {/* Loading State */}
          {isGenerating && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
              </div>
              <p className="text-gray-600 font-body">
                Crafting your viral content...
              </p>
              <div className="loading-dots mt-4 justify-center">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {/* Generated Content */}
          {generatedContent && (
            <div className="space-y-4">
              {/* Title */}
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-2xl font-bold text-gray-900">
                  {generatedContent.title}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="btn-secondary"
                  data-testid="copy-content-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy All
                    </>
                  )}
                </Button>
              </div>

              {/* Content Area */}
              <ScrollArea className="h-[300px] rounded-xl border border-gray-200 bg-white p-6">
                <div className="typewriter-text text-gray-800 text-sm leading-relaxed">
                  {displayedContent}
                  {isTyping && (
                    <span className="typewriter-cursor inline-block w-2 h-5 bg-[#7000FF] ml-1 align-middle" />
                  )}
                </div>
              </ScrollArea>

              {/* Viral Tip */}
              {!isTyping && generatedContent.viral_tip && (
                <motion.div
                  className="p-4 bg-gradient-to-r from-[#CCFF00]/20 to-[#7000FF]/20 rounded-xl border border-[#CCFF00]/30"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#CCFF00] flex items-center justify-center flex-shrink-0">
                      <Zap className="w-4 h-4 text-black" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-sm text-gray-900">
                        VIRAL TIP
                      </p>
                      <p className="text-sm text-gray-700 mt-1 font-body">
                        {generatedContent.viral_tip}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GenerationModal;
